/**
 * First-run clinic setup. Full screen, outside the dashboard shell: the owner
 * lands here after accepting the invitation and stays until the clinic can
 * take bookings. See ProtectedRoute for the redirect.
 */
export const setupRoutePath = '/setup';

// Finance routes
export const financeRoutePaths = {
  root: '/dashboard/finance',
  income: '/dashboard/finance/income',
  incomeImports: '/dashboard/finance/income/import',
  incomeImportJob: '/dashboard/finance/income/import/:jobId',
  expenses: '/dashboard/finance/expenses',
  expenseImports: '/dashboard/finance/expenses/import',
  expenseImportJob: '/dashboard/finance/expenses/import/:jobId',
  expenseDetails: '/dashboard/finance/expenses/:expenseId',
  payments: '/dashboard/finance/payments',
  invoiceDetails: '/dashboard/finance/invoices/:invoiceId',
  transactions: '/dashboard/finance/transactions',
  /** Treatment plans the patient pays on a schedule. */
  instalments: '/dashboard/finance/instalments',
  transactionDetails: '/dashboard/finance/transactions/:transactionId',
  monthlyReport: '/dashboard/finance/monthly-report',
  accounts: '/dashboard/finance/accounts',
  cogs: '/dashboard/finance/cogs',
  assets: '/dashboard/finance/assets',
  ownerEquity: '/dashboard/finance/owner-equity',
  practiceHealth: '/dashboard/finance/practice-health',
  profitLoss: '/dashboard/finance/profit-loss',
  balanceSheet: '/dashboard/finance/balance-sheet',
  cashFlow: '/dashboard/finance/cash-flow',
} as const;

// Reports routes (separate from finance — gated by 'reports' resource permission)
export const reportRoutePaths = {
  root: '/dashboard/reports',
  cashCollection: '/dashboard/reports/cash-collection',
  pendingPayments: '/dashboard/reports/pending-payments',
  profitLoss: '/dashboard/reports/profit-loss',
  ledger: '/dashboard/reports/ledger',
  kpi: '/dashboard/reports/kpi',
  expenseSummary: '/dashboard/reports/expense-summary',
  accountsBalance: '/dashboard/reports/accounts-balance',
  refunds: '/dashboard/reports/refunds',
  treatmentProfitability: '/dashboard/reports/treatment-profitability',
  patientIncome: '/dashboard/reports/patient-income',
  branchComparison: '/dashboard/reports/branches',
  arAging: '/dashboard/reports/ar-aging',
  patientStatement: '/dashboard/reports/patient-statement',
  production: '/dashboard/reports/production',
  offersLoyalty: '/dashboard/reports/offers-loyalty',
} as const;

// Inventory routes
export const inventoryRoutePaths = {
  stockLevels: '/dashboard/inventory/stock-levels',
  materialStock: '/dashboard/inventory/stock-levels/:materialId',
  purchases: '/dashboard/inventory/purchases',
  newPurchase: '/dashboard/inventory/purchases/new',
  newPurchaseAlt: '/inventory/purchases/new',
  /** The old detail address; still routed so existing links keep working. */
  purchaseDetails: '/inventory/purchases/:purchaseId',
  purchaseDetail: '/dashboard/inventory/purchases/:purchaseId',
  /** The name people use for the page; redirects to `purchases`. */
  purchasesAlias: '/dashboard/inventory/stock-purchases',
  consumption: '/dashboard/inventory/consumption',
  usage: '/dashboard/inventory/usage',
  stockCount: '/dashboard/inventory/stock-count',
  transfers: '/dashboard/inventory/transfers',
  suppliers: '/dashboard/inventory/suppliers',
  supplierDetail: '/dashboard/inventory/suppliers/:supplierId',
  /** The old addresses, outside /dashboard; they redirect. */
  suppliersLegacy: '/inventory/suppliers',
  supplierDetailLegacy: '/inventory/suppliers/:supplierId',
} as const;

/**
 * One material's stock page. `branch` scopes it to the line that was opened;
 * without it the page shows every branch.
 */
export function materialStockPath(
  materialId: string,
  branchId?: string,
): string {
  const base = `/dashboard/inventory/stock-levels/${materialId}`;
  return branchId ? `${base}?branch=${branchId}` : base;
}

export type SupplierTab = 'purchases' | 'catalog' | 'history' | 'payments';

/** One supplier's page, optionally on a tab. */
export function supplierDetailPath(
  supplierId: string,
  tab?: SupplierTab,
  extra?: Record<string, string>,
): string {
  const params = new URLSearchParams({ ...(tab ? { tab } : {}), ...extra });
  const qs = params.toString();
  return `/dashboard/inventory/suppliers/${supplierId}${qs ? `?${qs}` : ''}`;
}

/** One stock purchase's page. */
export function purchaseDetailPath(purchaseId: string): string {
  return `/dashboard/inventory/purchases/${purchaseId}`;
}

// Queue routes
export const queueRoutePaths = {
  root: '/dashboard/queue',
  receptionist: '/dashboard/queue/receptionist',
  doctor: '/dashboard/queue/doctor',
  /**
   * The waiting-room wall screen. Deliberately OUTSIDE /dashboard: it renders
   * without the sidebar, nav or header, and is opened full-screen on a kiosk
   * rather than navigated to from inside the app.
   */
  display: '/queue/display/:branchId',
} as const;

// Print routes
export const printRoutePaths = {
  receipt: '/print/receipt/:appointmentId',
  visitStatement: '/print/visit-statement/:appointmentId',
  invoice: '/print/invoice/:invoiceId',
  expense: '/print/expense/:expenseId',
  prescription: '/print/prescription/:prescriptionId',
  treatmentPlanStatement: '/print/treatment-plan-statement/:patientId',
  payslip: '/print/payslip/:payoutId',
  consent: '/print/consent/:consentId',
  planAcceptance:
    '/print/treatment-plan-acceptance/:patientId/:docId/:acceptanceId',
  patientCard: '/print/patient-card/:patientId',
} as const;

/**
 * The treatment plan as shown to the patient, full screen and outside the
 * app's shell, so the tablet can be handed across the chair.
 */
export const planPresentationPath = '/present/treatment-plan/:patientId/:docId';

/** The presentation of a plan; `returnTo` is where its Exit leads. */
export function planPresentationUrl(
  patientId: string,
  docId: string,
  returnTo?: string,
): string {
  const path = planPresentationPath
    .replace(':patientId', patientId)
    .replace(':docId', docId);
  return returnTo ? `${path}?returnTo=${encodeURIComponent(returnTo)}` : path;
}

/**
 * The page a consent signing link opens. Outside /dashboard and outside the
 * auth guard on purpose: the patient opening it has no account. The API
 * builds the same path when it issues a link.
 */
export const consentSignPath = '/consent/sign/:token';

/**
 * The page a treatment plan review link opens, on the patient's own phone.
 * Public like the consent link; the API builds the same path.
 */
export const planReviewPath = '/plan/review/:token';

/**
 * The page a medical history link opens, on the patient's own phone. Public
 * like the consent link; the API builds the same path.
 */
export const medicalFormPath = '/medical-form/:token';

/** Printed on patient cards, so it has to keep working: never rename it. */
export const patientQrRoutePath = '/p/:patientId';

// Lab routes
export const labRoutePaths = {
  root: '/dashboard/labs',
  requests: '/dashboard/labs/requests',
  labsList: '/dashboard/labs/list',
  labDetails: '/dashboard/labs/partners/:labId',
} as const;

// Payroll routes
export const payrollRoutePaths = {
  root: '/dashboard/payroll',
  payoutDetails: '/dashboard/payroll/payouts/:id',
  /**
   * A clinician's own earnings. Deliberately outside the `payroll` permission:
   * seeing your own pay is a different right from seeing the clinic's.
   */
  myEarnings: '/dashboard/my-earnings',
} as const;

// Main dashboard routes
export const dashboardRoutePaths = {
  root: '/dashboard',
  home: '/dashboard/home',
  patients: '/dashboard/patients',
  patientImports: '/dashboard/patients/import',
  patientImportJob: '/dashboard/patients/import/:jobId',
  patientProfile: '/dashboard/patients/:patientId',
  patientLedger: '/dashboard/patients/:patientId/ledger',
  /** The appointments calendar. */
  reservations: '/dashboard/reservations',
  /** The appointments list: search, status tabs, bulk actions. */
  reservationsList: '/dashboard/reservations/list',
  pendingReservations: '/dashboard/reservations/pending',
  waitlist: '/dashboard/waitlist',
  recalls: '/dashboard/recalls',
  reservationDetails: '/dashboard/reservations/:appointmentId',
  reservationReceipt: '/dashboard/reservations/:appointmentId/receipt',
  procedureDetails:
    '/dashboard/reservations/:appointmentId/procedures/:procedureId',
  materials: '/dashboard/materials',
  labRequests: '/dashboard/lab-requests',
  labRequestDetails: '/dashboard/lab-requests/:labRequestId',
  chat: '/dashboard/chat',
  feedback: '/dashboard/feedback',
  feedbackDetail: '/dashboard/feedback/:id',
  settings: '/dashboard/settings',
  settingsPrescriptions: '/dashboard/settings/prescriptions',
  settingsSection: '/dashboard/settings/:section',
  activityLog: '/dashboard/activity-log',
  advancedAnalytics: '/dashboard/analytics-dashboard',
  referralSources: '/dashboard/referral-sources',
  patientRiskReport: '/dashboard/patient-risk-report',
  notifications: '/dashboard/notifications',
  insurance: '/dashboard/insurance',
  insuranceClaims: '/dashboard/insurance/claims',
  insuranceClaimDetail: '/dashboard/insurance/claims/:id',
  insuranceBatches: '/dashboard/insurance/batches',
  insuranceCompanies: '/dashboard/insurance/companies',
  insuranceCompanyDetail: '/dashboard/insurance/companies/:companyId',
  insurancePlanDetail:
    '/dashboard/insurance/companies/:companyId/plans/:planId',
} as const;

/**
 * Builders for the two Insurance detail routes.
 *
 * Both views used to be `useState` inside one component, so the URL never
 * changed: the browser's back button left the page entirely, a company could
 * not be linked to or bookmarked, and the Overview's own drill-through to an
 * insurer (`?companyId=…`) pointed at a parameter nothing read. Building the
 * paths here keeps every caller honest about the shape.
 */
export const insuranceCompanyPath = (companyId: string) =>
  `/dashboard/insurance/companies/${companyId}`;

export const insurancePlanPath = (companyId: string, planId: string) =>
  `/dashboard/insurance/companies/${companyId}/plans/${planId}`;

/**
 * Settings sections, as real paths.
 *
 * These were `?tab=` query params, which cost the app three things: the whole
 * settings bundle loaded on every visit (one component with a 19-way `switch`
 * cannot code-split), the shell re-rendered on every tab change, and a section
 * could not own a sub-route — so a staff member had to be a modal rather than
 * a page. `settingsSectionPath('staff')` is the only way to build one of these
 * links; nothing should concatenate the string by hand.
 */
export const SETTINGS_SECTIONS = [
  'subscription',
  'profile',
  'notifications',
  'clinic',
  'availability',
  'booking',
  'landing-page',
  'branches',
  'rooms',
  'staff',
  'roles',
  'procedures',
  'recalls',
  'offers',
  'loyalty',
  'reservation-fees',
  'medical-history',
  'prescriptions',
  'consent-forms',
  'finance',
  'receipts',
  'refunds',
  'plan-payments',
  'currency',
  'xray-importer',
] as const;

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

export function settingsSectionPath(section: SettingsSection): string {
  return `${dashboardRoutePaths.settings}/${section}`;
}

export const settingsRoutePaths = {
  root: dashboardRoutePaths.settings,
  staffDetails: `${dashboardRoutePaths.settings}/staff/:staffId`,
  roleEditor: `${dashboardRoutePaths.settings}/roles/:roleId`,
  procedureEditor: `${dashboardRoutePaths.settings}/procedures/:procedureId`,
} as const;

export function staffDetailsPath(staffId: string): string {
  return `${dashboardRoutePaths.settings}/staff/${staffId}`;
}

/**
 * The role editor is a page, not a modal.
 *
 * Sixty permissions with search, a live diff and an affected-members warning
 * do not fit a dialog, and a route means an editor in progress survives a
 * refresh and can be linked to. `new` is the create form; any other value is
 * a role id.
 */
export function roleEditorPath(roleId: string | 'new'): string {
  return `${dashboardRoutePaths.settings}/roles/${roleId}`;
}

/**
 * The procedure editor is a page, for the same reason the role editor is.
 *
 * Its three sections — details, default materials, lab requirements — used to
 * be three separate row-level modals, which meant the two things that inform
 * a price (what the materials cost, what the lab charges) could never be seen
 * next to the price itself. A route puts them on one surface with room for
 * the live margin preview, survives a refresh mid-edit, and can be linked to.
 * `new` is the create form; any other value is a procedure id.
 */
export function procedureEditorPath(procedureId: string | 'new'): string {
  return `${dashboardRoutePaths.settings}/procedures/${procedureId}`;
}

/**
 * Settings segments that have been renamed, old value to new.
 *
 * `clinics` rendered a page titled "Rooms" in every language: the segment
 * carried the DATABASE name for the entity (table `clinics`, `roomId` FKs)
 * while the product has always called them rooms, and `settings.clinic`
 * separately calls itself "Clinic". The path now matches what the page says.
 *
 * The database name is deliberately left alone — renaming the table and four
 * FK columns is not a settings-navigation change.
 */
export const RENAMED_SETTINGS_SECTIONS: Record<string, SettingsSection> = {
  clinics: 'rooms',
  // Password and sessions moved into Profile, which is where people looked for
  // them; a second page offering half of the same controls is what split the
  // save models in the first place.
  security: 'profile',
  // The brand colour was briefly a section of its own. A whole settings page
  // for one field, a click away from the logo it has to agree with, is not a
  // section — it is a card on the Clinic page, which is where it now lives.
  appearance: 'clinic',
};

/**
 * Settings segments that are no longer settings, old segment to the page that
 * replaced it. The redirect keeps the query string: the Home attention list
 * and older notifications link to `activity-log?severity=…&from=…`.
 *
 * The activity log is a record of what happened, read while investigating —
 * nothing on it configures the clinic — so it moved to the main navigation.
 */
export const MOVED_SETTINGS_SECTIONS: Record<string, string> = {
  'activity-log': dashboardRoutePaths.activityLog,
};

/** The current segment for a possibly-renamed one. */
export function canonicalSettingsSection(
  segment: string | null | undefined,
): SettingsSection | null {
  if (!segment) return null;
  const renamed = RENAMED_SETTINGS_SECTIONS[segment];
  if (renamed) return renamed;
  return (SETTINGS_SECTIONS as readonly string[]).includes(segment)
    ? (segment as SettingsSection)
    : null;
}

/**
 * The section a legacy `?tab=` value maps to. Identity for all current tabs —
 * the query values and the path segments were deliberately kept the same so
 * the redirect is total and no bookmark 404s. Renamed segments resolve through
 * `canonicalSettingsSection`, so `?tab=clinics` still lands on Rooms.
 */
export function sectionFromLegacyTab(
  tab: string | null,
): SettingsSection | null {
  return canonicalSettingsSection(tab);
}

// Helper to check if a path requires clinic
export function requiresClinic(path: string): boolean {
  const noClinicRequired = [
    '/verify-email',
    '/select-clinic',
    '/',
    '/login',
    '/forgot-password',
    '/reset-password',
    '/signup',
    '/dental-chart-demo',
  ];
  return !noClinicRequired.includes(path);
}

// Helper to check if a path is public
export function isPublicPath(path: string): boolean {
  const publicPaths = [
    '/',
    '/login',
    '/forgot-password',
    '/reset-password',
    '/signup',
    '/dental-chart-demo',
  ];
  return publicPaths.includes(path);
}
